// Catches a routed screen failing: a lazy chunk that will not load (a tab older than the deploy) goes
// to lib/chunkRecovery's single guarded reload, and any other render error shows a calm fallback with
// Retry and Home instead of the blank page an uncaught error leaves. `env` is a test seam for the
// reload. A class because React has no hook for error boundaries.
import { Component, type ReactNode } from "react";
import {
  isChunkLoadError,
  recoverFromChunkError,
  type ChunkRecoveryEnv,
} from "../lib/chunkRecovery";
import { useT } from "../lib/i18n";
import { navigate } from "../lib/router";
import { Button, EmptyState, Screen } from "../ui";

interface State {
  readonly error: unknown;
  readonly failed: boolean;
}

/** What a screen that failed to render shows; Retry asks the screen to render again. */
function RouteError({ onRetry }: { onRetry: () => void }): React.JSX.Element {
  const { t } = useT();
  return (
    <Screen data-testid="route-error">
      <EmptyState
        icon="alert"
        role="alert"
        title={t("route.error.title")}
        body={t("route.error.body")}
        action={
          <>
            <Button tone="primary" label={t("route.error.retry")} onPress={onRetry} />
            <Button
              tone="ghost"
              label={t("route.error.home")}
              onPress={() => {
                navigate("/");
              }}
            />
          </>
        }
      />
    </Screen>
  );
}

export class ChunkErrorBoundary extends Component<
  { children: ReactNode; env?: ChunkRecoveryEnv; resetKey?: string },
  State
> {
  override state: State = { error: undefined, failed: false };

  static getDerivedStateFromError(error: unknown): State {
    return { error, failed: true };
  }

  override componentDidCatch(error: unknown): void {
    recoverFromChunkError(error, this.props.env);
  }

  // A new route clears a failure left by the last one; a key would remount every screen instead.
  override componentDidUpdate(previous: { resetKey?: string }): void {
    if (this.state.failed && previous.resetKey !== this.props.resetKey) this.retry();
  }

  override render(): ReactNode {
    if (!this.state.failed) return this.props.children;
    // Reloading: keep the page empty for the moment it takes.
    if (
      isChunkLoadError(this.state.error) &&
      recoverFromChunkError(this.state.error, this.props.env)
    ) {
      return null;
    }
    return <RouteError onRetry={this.retry} />;
  }

  private readonly retry = (): void => {
    this.setState({ error: undefined, failed: false });
  };
}
